"use client";

import { useId } from "react";
import { ExternalLink, Mail, Send } from "lucide-react";
import { usePermissions } from "@/hooks/use-permissions";
import { NOTIFICATIONS_PERMISSIONS } from "@/lib/auth/permissions.constants";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { useNotificationChannelOptions } from "@/hooks/notifications/use-channel-options";
import { NOTIFICATION_CHANNELS_HREF } from "@/lib/notifications/page-link";

/**
 * Opens in a new tab on purpose: the picker lives inside an unsaved agent
 * settings form, and navigating away in place would discard pending edits.
 */
function ManageChannelsLink({ label }: { label: string }) {
  return (
    <a
      className="inline-flex items-center gap-0.5 underline underline-offset-2 hover:text-foreground transition-colors"
      href={NOTIFICATION_CHANNELS_HREF}
      target="_blank"
      rel="noopener noreferrer"
    >
      {label}
      <ExternalLink className="h-3 w-3 shrink-0" aria-hidden="true" />
    </a>
  );
}

interface HandoverChannelPickerProps {
  /** Selected channel ids (`human_handover.notification_channel_ids`). */
  value: string[];
  onChange: (ids: string[]) => void;
  disabled?: boolean;
  /** Replaces the default CRM-lead sentence; the "manage channels" link stays either way. */
  footerHint?: string;
}

/**
 * Per-agent picker of the tenant's notification channels for handover alerts.
 * Empty selection = no external notification (the CRM action is separate). Shared
 * by the email + messaging agent settings; reads the minimal channel options
 * endpoint (reachable by agent managers, unlike the full config list).
 */
export function HandoverChannelPicker({
  value,
  onChange,
  disabled,
  footerHint,
}: HandoverChannelPickerProps) {
  const { can } = usePermissions();
  const canManageChannels = can(NOTIFICATIONS_PERMISSIONS.MANAGE);
  const { options, loading } = useNotificationChannelOptions();
  // The same page can mount this twice (handover + paused-conversation alert), so checkbox ids
  // must be instance-scoped — a hardcoded prefix would collide and break both labels.
  const idPrefix = useId();

  const toggle = (id: string) => {
    onChange(value.includes(id) ? value.filter((v) => v !== id) : [...value, id]);
  };

  if (loading) {
    return <div className="h-16 animate-pulse rounded-md bg-muted" />;
  }

  if (options.length === 0) {
    // The Notification Channels screen is notifications.manage-only; an agent
    // manager without it can't open it, so only link there when allowed.
    return (
      <p className="text-sm text-muted-foreground">
        <span>No notification channels yet.</span>{" "}
        {canManageChannels ? (
          <>
            <ManageChannelsLink label="Create one" /> on the Notification Channels screen,
            then select it here.
          </>
        ) : (
          <>Ask an admin to add one on the Notification Channels screen.</>
        )}
      </p>
    );
  }

  return (
    <div className="space-y-1">
      {options.map((opt) => {
        const id = `${idPrefix}-${opt.id}`;
        return (
          <Label
            key={opt.id}
            htmlFor={id}
            className="flex items-center gap-2 rounded-sm px-2 py-1.5 text-sm font-normal hover:bg-muted cursor-pointer"
          >
            <Checkbox
              id={id}
              checked={value.includes(opt.id)}
              onCheckedChange={() => toggle(opt.id)}
              disabled={disabled}
            />
            {opt.type === "email" ? (
              <Mail className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
            ) : (
              <Send className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
            )}
            <span className="min-w-0 flex-1 truncate">{opt.name}</span>
            {opt.isSystem && <Badge variant="outline">system</Badge>}
            {!opt.enabled && <Badge variant="outline">disabled</Badge>}
          </Label>
        );
      })}
      <p className="px-2 pt-1 text-xs text-muted-foreground">
        {footerHint ?? "Empty = no external alert (the CRM lead is configured separately)."}
        {canManageChannels && (
          <>
            {" "}
            <ManageChannelsLink label="Add or edit channels" />
          </>
        )}
      </p>
    </div>
  );
}
